import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, Ticket, Users } from "lucide-react";
import { motion } from "framer-motion";
import { api, apiError, imgUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const PALETTE = ["#F7E3D7", "#E4EDDF", "#F5EED8", "#E7E3F2"];
const inputCls = "w-full bg-white/80 border border-[#1A1A1A]/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]/40 transition-colors placeholder:text-[#9A9A94]";

export default function Workshops() {
  const [workshops, setWorkshops] = useState([]);
  const [comingSoon, setComingSoon] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [doneFor, setDoneFor] = useState(null);

  const load = () => api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setComingSoon(!!r.data.workshops_coming_soon)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [wlEmail, setWlEmail] = useState("");
  const [wlLoading, setWlLoading] = useState(false);
  const [wlDone, setWlDone] = useState(false);

  const register = async (wid) => {
    setLoading(true);
    try {
      const r = await api.post("/workshop-registrations", { workshop_id: wid, ...form, origin_url: window.location.origin });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setDoneFor(wid);
      setOpenFor(null);
      setForm({ name: "", email: "", phone: "", message: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!wlEmail) return;
    setWlLoading(true);
    try {
      await api.post("/workshop-waitlist", { email: wlEmail });
      setWlDone(true);
      setWlEmail("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setWlLoading(false);
    }
  };

  return (
    <main data-testid="workshops-page" className="pt-32 pb-28 overflow-hidden">
      <Seo title="Workshops — Bahare | Group Photography Workshops in Los Angeles"
        description="Small-group photography workshops in Los Angeles — foundations, mobile photography for business and content creation days." />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal className="text-center">
          <motion.span initial={{ rotate: -6, scale: 0.8, opacity: 0 }} animate={{ rotate: -4, scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block bg-[#E4572E] text-[#F9F9F8] text-[10px] uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8">
            In person · Los Angeles
          </motion.span>
          <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[1.02]">
            Learn together,<br /><span className="italic text-[#E4572E]">make things.</span>
          </h1>
          <p className="mt-6 text-sm text-[#4A4A4A] max-w-md mx-auto leading-relaxed">
            Small-group workshops — limited seats, hands-on practice, honest feedback, and a room full of people who love creating.
          </p>
        </Reveal>

        {comingSoon ? (
          <Reveal className="mt-20">
            <div className="rounded-[2rem] px-8 py-16 md:py-24 text-center" style={{ backgroundColor: "#F7E3D7" }} data-testid="workshops-coming-soon">
              <span className="inline-block bg-[#E4572E] text-[#F9F9F8] text-[10px] uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8">
                Workshops
              </span>
              <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
                Coming <span className="italic text-[#E4572E]">soon</span>
              </h2>
              <p className="mt-6 text-sm text-[#4A4A4A] max-w-md mx-auto leading-relaxed">
                New workshops are being planned right now. Leave your email and I'll let you know as soon as one opens.
              </p>
              {wlDone ? (
                <p data-testid="waitlist-success" className="mt-8 text-sm bg-white/70 rounded-2xl px-6 py-5 max-w-md mx-auto">
                  You're on the list — I'll email you as soon as a workshop opens.
                </p>
              ) : (
                <form onSubmit={joinWaitlist} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
                  <input type="email" required placeholder="Your email address" value={wlEmail} onChange={(e) => setWlEmail(e.target.value)}
                    data-testid="waitlist-email" className={`${inputCls} flex-1 !rounded-full`} />
                  <button type="submit" disabled={wlLoading} data-testid="waitlist-submit"
                    className="bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] px-8 py-3.5 rounded-full hover:bg-[#E4572E] transition-colors disabled:opacity-50">
                    {wlLoading ? "Joining…" : "Notify Me"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        ) : (
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-20">
          {workshops.map((w, i) => (
            <Reveal key={w.id} delay={(i % 2) * 0.1}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`workshop-${w.id}`}
                className="rounded-[2rem] overflow-hidden h-full flex flex-col"
                style={{ backgroundColor: PALETTE[i % PALETTE.length] }}>
                {w.image && (
                  <div className="overflow-hidden">
                    <img src={imgUrl(w.image)} alt={w.title} loading="lazy"
                      className="w-full aspect-[16/9] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-out" />
                  </div>
                )}
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-2 mb-5">
                    {w.date && (
                      <span className="inline-flex items-center gap-1.5 bg-white/70 text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full" data-testid={`workshop-date-${w.id}`}>
                        <CalendarDays size={12} strokeWidth={1.5} /> {w.date}
                      </span>
                    )}
                    {w.price && (
                      <span className="inline-flex items-center gap-1.5 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full" data-testid={`workshop-price-${w.id}`}>
                        <Ticket size={12} strokeWidth={1.5} /> {w.price}
                      </span>
                    )}
                    {w.capacity > 0 && (
                      <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full ${w.seats_left > 0 ? "bg-white/70" : "bg-[#E4572E] text-[#F9F9F8]"}`} data-testid={`workshop-seats-${w.id}`}>
                        <Users size={12} strokeWidth={1.5} /> {w.seats_left > 0 ? `${w.seats_left}/${w.capacity} seats left` : "Full"}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl font-light mb-3">{w.title}</h3>
                  {w.description && <p className="text-sm text-[#3d3d3a] leading-relaxed mb-8">{w.description}</p>}

                  <div className="mt-auto">
                    {doneFor === w.id ? (
                      <p data-testid={`workshop-success-${w.id}`} className="text-sm bg-white/70 rounded-2xl p-5">
                        Thank you — your seat request was received. I'll confirm shortly.
                      </p>
                    ) : w.seats_left === 0 ? (
                      <p className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">This workshop is full</p>
                    ) : openFor === w.id ? (
                      <div className="bg-white/60 rounded-2xl p-5 space-y-3" data-testid={`workshop-form-${w.id}`}>
                        <input placeholder="Name *" value={form.name} onChange={set("name")} data-testid={`ws-name-${w.id}`} className={inputCls} />
                        <input placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid={`ws-email-${w.id}`} className={inputCls} />
                        <input placeholder="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} data-testid={`ws-phone-${w.id}`} className={inputCls} />
                        <textarea placeholder="Message (optional)" rows={2} value={form.message} onChange={set("message")} data-testid={`ws-message-${w.id}`} className={`${inputCls} resize-none`} />
                        <div className="flex gap-3 pt-1">
                          <button onClick={() => register(w.id)} disabled={loading || !form.name || !form.email}
                            data-testid={`workshop-register-${w.id}`}
                            className="flex-1 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-3.5 rounded-full hover:bg-[#E4572E] transition-colors disabled:opacity-50">
                            {loading ? "Sending…" : (w.price ? `Pay ${w.price} & Register` : "Register")}
                          </button>
                          <button onClick={() => setOpenFor(null)}
                            className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A] hover:text-[#1A1A1A] px-3">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setOpenFor(w.id)} data-testid={`workshop-join-${w.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#E4572E] transition-colors">
                        {w.price ? `Book My Seat — ${w.price}` : "Book My Seat"} <ArrowRight size={13} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
        )}
        {!comingSoon && workshops.length === 0 && <p className="text-sm text-[#7A7A7A] text-center mt-20">New workshops are being scheduled.</p>}

        <Reveal className="text-center mt-20">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7A7A7A]">Secure online payment · Card, Apple Pay & more</p>
        </Reveal>
      </div>
    </main>
  );
}
